import { ServiceStoreConfig } from "../types/ServiceStoreTypes";

/**
 * Financial Module Service Stores
 * Updated to match backend api-types
 */
export const financialStores: ServiceStoreConfig[] = [
  {
    name: "accounts-payable",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.accountsPayableStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalAP = entities.length;
          const unpaid = entities.filter(
            (ap: Record<string, unknown>) => ap.paymentStatus === "unpaid" || ap.paymentStatus === "pending"
          ).length;
          const overdue = entities.filter(
            (ap: Record<string, unknown>) => {
              const dueDate = new Date(ap.dueDate as string);
              return dueDate < new Date() && ap.paymentStatus !== "paid";
            }
          ).length;
          const totalAmount = entities.reduce(
            (sum: number, ap: Record<string, unknown>) => sum + ((ap.amountDue as number) || 0),
            0
          );

          return [
            {
              title: "Total AP",
              value: () => totalAP.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Unpaid",
              value: () => unpaid.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Overdue",
              value: () => overdue.toString(),
              valueColor: "error.main",
            },
            {
              title: "Total Amount",
              value: () => `$${((totalAmount as number) / 1000).toFixed(0)}K`,
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "accounts-receivable",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.accountsReceivableStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalAR = entities.length;
          const unpaid = entities.filter(
            (ar: Record<string, unknown>) => ar.paymentStatus === "unpaid" || ar.paymentStatus === "pending"
          ).length;
          const overdue = entities.filter(
            (ar: Record<string, unknown>) => {
              const dueDate = new Date(ar.dueDate as string);
              return dueDate < new Date() && ar.paymentStatus !== "paid";
            }
          ).length;
          const totalAmount = entities.reduce(
            (sum: number, ar: Record<string, unknown>) => sum + ((ar.amountDue as number) || 0),
            0
          );

          return [
            {
              title: "Total AR",
              value: () => totalAR.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Unpaid",
              value: () => unpaid.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Overdue",
              value: () => overdue.toString(),
              valueColor: "error.main",
            },
            {
              title: "Total Amount",
              value: () => `$${((totalAmount as number) / 1000).toFixed(0)}K`,
              valueColor: "success.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "financial-summary",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
    },
  },
  {
    name: "general-ledger",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.generalLedgerStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalEntries = entities.length;
          const debits = entities.reduce(
            (sum: number, entry: Record<string, unknown>) => sum + ((entry.debitAmount as number) || 0),
            0
          );
          const credits = entities.reduce(
            (sum: number, entry: Record<string, unknown>) => sum + ((entry.creditAmount as number) || 0),
            0
          );
          const unreconciled = entities.filter(
            (entry: Record<string, unknown>) => !entry.reconciled
          ).length;

          return [
            {
              title: "Total Entries",
              value: () => totalEntries.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Total Debits",
              value: () => `$${((debits as number) / 1000).toFixed(0)}K`,
              valueColor: "error.main",
            },
            {
              title: "Total Credits",
              value: () => `$${((credits as number) / 1000).toFixed(0)}K`,
              valueColor: "success.main",
            },
            {
              title: "Unreconciled",
              value: () => unreconciled.toString(),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: true,
        },
      },
    },
  },
  // §1.11: `invoices` bare registration removed — the accounting module now
  // registers `accounting-invoices` (→ accountingInvoicesStore). See RootStore alias.
  {
    name: "invoice-line-items",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
    },
  },
  {
    name: "payments-made",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.paymentsMadeStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalPayments = entities.length;
          const thisMonth = entities.filter(
            (payment: Record<string, unknown>) => {
              const paymentDate = new Date(payment.paymentDate as string);
              const now = new Date();
              return paymentDate.getMonth() === now.getMonth() && paymentDate.getFullYear() === now.getFullYear();
            }
          ).length;
          const totalAmount = entities.reduce(
            (sum: number, payment: Record<string, unknown>) => sum + ((payment.paymentAmount as number) || 0),
            0
          );
          const pending = entities.filter(
            (payment: Record<string, unknown>) => payment.paymentStatus === "pending"
          ).length;

          return [
            {
              title: "Total Payments",
              value: () => totalPayments.toString(),
              valueColor: "primary.main",
            },
            {
              title: "This Month",
              value: () => thisMonth.toString(),
              valueColor: "info.main",
            },
            {
              title: "Total Amount",
              value: () => `$${((totalAmount as number) / 1000).toFixed(0)}K`,
              valueColor: "error.main",
            },
            {
              title: "Pending",
              value: () => pending.toString(),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "payments-received",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.paymentsReceivedStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalPayments = entities.length;
          const thisMonth = entities.filter(
            (payment: Record<string, unknown>) => {
              const paymentDate = new Date(payment.paymentDate as string);
              const now = new Date();
              return paymentDate.getMonth() === now.getMonth() && paymentDate.getFullYear() === now.getFullYear();
            }
          ).length;
          const totalAmount = entities.reduce(
            (sum: number, payment: Record<string, unknown>) => sum + ((payment.paymentAmount as number) || 0),
            0
          );
          const pending = entities.filter(
            (payment: Record<string, unknown>) => payment.paymentStatus === "pending"
          ).length;

          return [
            {
              title: "Total Payments",
              value: () => totalPayments.toString(),
              valueColor: "primary.main",
            },
            {
              title: "This Month",
              value: () => thisMonth.toString(),
              valueColor: "info.main",
            },
            {
              title: "Total Amount",
              value: () => `$${((totalAmount as number) / 1000).toFixed(0)}K`,
              valueColor: "success.main",
            },
            {
              title: "Pending",
              value: () => pending.toString(),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "pcos",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.pcosStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalPCOs = entities.length;
          const pending = entities.filter(
            (pco: Record<string, unknown>) => pco.status === "pending"
          ).length;
          const approved = entities.filter(
            (pco: Record<string, unknown>) => pco.status === "approved"
          ).length;
          const totalValue = entities.reduce(
            (sum: number, pco: Record<string, unknown>) => sum + ((pco.changeAmount as number) || 0),
            0
          );

          return [
            {
              title: "Total PCOs",
              value: () => totalPCOs.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Pending",
              value: () => pending.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Approved",
              value: () => approved.toString(),
              valueColor: "success.main",
            },
            {
              title: "Total Value",
              value: () => `$${((totalValue as number) / 1000).toFixed(0)}K`,
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "pco-line-items",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
    },
  },
  // §1.11: `purchase-orders` bare registration removed — the accounting module now
  // registers `accounting-purchase-orders` (→ accountingPurchaseOrdersStore). See RootStore alias.
  {
    name: "po-line-items",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
    },
  },
  {
    name: "project-budget",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.projectBudgetStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalBudgets = entities.length;
          const totalBudgetAmount = entities.reduce(
            (sum: number, budget: Record<string, unknown>) => sum + ((budget.budgetAmount as number) || 0),
            0
          );
          const totalSpent = entities.reduce(
            (sum: number, budget: Record<string, unknown>) => sum + ((budget.actualCost as number) || 0),
            0
          );
          const overbudget = entities.filter(
            (budget: Record<string, unknown>) => (budget.actualCost as number) > (budget.budgetAmount as number)
          ).length;

          return [
            {
              title: "Total Budgets",
              value: () => totalBudgets.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Total Budget",
              value: () => `$${((totalBudgetAmount as number) / 1000).toFixed(0)}K`,
              valueColor: "info.main",
            },
            {
              title: "Total Spent",
              value: () => `$${((totalSpent as number) / 1000).toFixed(0)}K`,
              valueColor: "warning.main",
            },
            {
              title: "Over Budget",
              value: () => overbudget.toString(),
              valueColor: "error.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "project-costs",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.projectCostsStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalCosts = entities.length;
          const totalAmount = entities.reduce(
            (sum: number, cost: Record<string, unknown>) => sum + ((cost.costAmount as number) || 0),
            0
          );
          const thisMonth = entities.filter(
            (cost: Record<string, unknown>) => {
              const costDate = new Date(cost.costDate as string);
              const now = new Date();
              return costDate.getMonth() === now.getMonth() && costDate.getFullYear() === now.getFullYear();
            }
          ).length;
          const approved = entities.filter(
            (cost: Record<string, unknown>) => cost.approvalStatus === "approved"
          ).length;

          return [
            {
              title: "Total Costs",
              value: () => totalCosts.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Total Amount",
              value: () => `$${((totalAmount as number) / 1000).toFixed(0)}K`,
              valueColor: "error.main",
            },
            {
              title: "This Month",
              value: () => thisMonth.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Approved",
              value: () => approved.toString(),
              valueColor: "success.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "quotes",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.quotesStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalQuotes = entities.length;
          const pending = entities.filter(
            (quote: Record<string, unknown>) => quote.status === "pending" || quote.status === "draft"
          ).length;
          const accepted = entities.filter(
            (quote: Record<string, unknown>) => quote.status === "accepted"
          ).length;
          const totalValue = entities.reduce(
            (sum: number, quote: Record<string, unknown>) => sum + ((quote.totalAmount as number) || 0),
            0
          );

          return [
            {
              title: "Total Quotes",
              value: () => totalQuotes.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Pending",
              value: () => pending.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Accepted",
              value: () => accepted.toString(),
              valueColor: "success.main",
            },
            {
              title: "Total Value",
              value: () => `$${((totalValue as number) / 1000).toFixed(0)}K`,
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "quote-line-items",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
    },
  },
  {
    name: "schedule-of-values",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.scheduleOfValuesStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalItems = entities.length;
          const totalScheduled = entities.reduce(
            (sum: number, item: Record<string, unknown>) => sum + ((item.scheduledValue as number) || 0),
            0
          );
          const totalCompleted = entities.reduce(
            (sum: number, item: Record<string, unknown>) => sum + ((item.previouslyCompleted as number) || 0),
            0
          );
          const percentComplete = totalScheduled > 0 ? (totalCompleted / totalScheduled) * 100 : 0;

          return [
            {
              title: "Total Items",
              value: () => totalItems.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Scheduled Value",
              value: () => `$${((totalScheduled as number) / 1000).toFixed(0)}K`,
              valueColor: "info.main",
            },
            {
              title: "Completed",
              value: () => `$${((totalCompleted as number) / 1000).toFixed(0)}K`,
              valueColor: "success.main",
            },
            {
              title: "% Complete",
              value: () => `${percentComplete.toFixed(1)}%`,
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
];
